/**
 * Filter, sort and grouping logic of the Library (library.md §2–§4).
 * Pure functions; state lives in src/pages/Library.tsx.
 *
 * Query-param contract emitted by Dashboard/Report:
 *   /library?year=YYYY&month=1-12&category=X&complications=1&newTechniques=1&enrich=1
 */
import type { Surgery, Role, Technique } from '@/lib/types';
import { ROLES, TECHNIQUES, LEGACY_ROLE_VALUES, normalizeTechnique } from '@/lib/types';
import { needsEnrichment } from '@/lib/enrichment-queue';
import { isPatientIdHash, patientIdLookupToken } from '@/lib/patient-id';
import { yearOf, monthOf, newTechniques } from '@/lib/stats';

export type SortOrder = 'recent' | 'oldest' | 'duration' | 'folio';
export type ComplicationFilter = 'all' | 'with' | 'without';

export interface LibraryFilters {
  query: string;
  /** selected years (empty = all) */
  years: number[];
  /** month 1–12 (arrives via query param; combinable with year) */
  month: number | null;
  /** Inclusive range start `YYYY-MM-DD` (clears month when set in UI). */
  dateFrom: string | null;
  /** Inclusive range end `YYYY-MM-DD`. */
  dateTo: string | null;
  category: string | null;
  techniques: Technique[];
  role: Role | 'all';
  complications: ComplicationFilter;
  /** year of the "new techniques" filter (query param newTechniques=1) */
  newTechniquesYear: number | null;
  /** Career-import skeletons still missing technique steps (`enrich=1`). */
  needsEnrichment: boolean;
}

export const EMPTY_FILTERS: LibraryFilters = {
  query: '',
  years: [],
  month: null,
  dateFrom: null,
  dateTo: null,
  category: null,
  techniques: [],
  role: 'all',
  complications: 'all',
  newTechniquesYear: null,
  needsEnrichment: false,
};

/** Optional needles for sealed patient-ID search (hashed query). */
export type QueryMatchOptions = {
  patientIdHash?: string | null;
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(value: string | null | undefined): value is string {
  return typeof value === 'string' && ISO_DATE_RE.test(value);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Local calendar day as `YYYY-MM-DD` (not UTC — matches the date inputs). */
export function todayIso(now = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
}

export function isTodayIso(value: string | null | undefined, now = new Date()): boolean {
  return isIsoDate(value) && value === todayIso(now);
}

/**
 * Maps toolbar year/month selection to an inclusive range ending today.
 * Empty years → no range (month-only keeps the month facet without a drawer range).
 */
export function dateRangeFromYearsMonth(
  years: number[],
  month: number | null,
  now = new Date(),
): { dateFrom: string | null; dateTo: string | null } {
  if (years.length === 0) return { dateFrom: null, dateTo: null };
  const startYear = Math.min(...years);
  const to = todayIso(now);
  if (month != null && month >= 1 && month <= 12) {
    return { dateFrom: `${startYear}-${pad2(month)}-01`, dateTo: to };
  }
  return { dateFrom: `${startYear}-01-01`, dateTo: to };
}

/** Sort options; labels resolve via t('library.sortOrders.<value>') */
export const SORT_ORDERS: { value: SortOrder }[] = [
  { value: 'recent' },
  { value: 'oldest' },
  { value: 'duration' },
  { value: 'folio' },
];

/** English-only labels for non-translated status lines (library.tsx). */
export const SORT_LABEL: Record<SortOrder, string> = {
  recent: 'most recent',
  oldest: 'oldest',
  duration: 'duration',
  folio: 'folio',
};

/** Side-band color of the volume card per technique (§5) */
export const BAND_COLOR: Record<Technique, string> = {
  Open: 'bg-pizarra',
  Laparoscopic: 'bg-acento',
  Robotic: 'bg-ambar',
  Endoscopic: 'bg-salvia',
  Other: 'bg-linea-fuerte',
};

/* ────────────────────────── search ────────────────────────── */

/** lowercase without diacritics: "Cholecystectomy" ≈ "cholecystectomy" */
export function normalize(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

/** Strip leading mask dots so "····7394" / "...7394" still match the hint. */
export function normalizePatientIdQuery(query: string): string {
  return normalize(query.trim()).replace(/^[·.•\s]+/, '');
}

/** Flatten free-text / tag fields a diary search may hit. */
export function surgerySearchHaystack(c: Surgery): string[] {
  const seg = c.seguimiento;
  const ficha = c.ficha;
  return [
    c.diagnostico,
    c.folio,
    c.paciente.iniciales,
    c.categoria,
    c.descripcion,
    c.tecnica,
    c.rol,
    c.hallazgos ?? '',
    c.lecciones,
    c.complicaciones.descripcion ?? '',
    c.complicaciones.grado ?? '',
    c.snomedCt?.term ?? '',
    ...(c.pasos ?? []),
    ...c.materiales,
    seg?.notasSeguimiento ?? '',
    seg?.anatomiaPatologica ?? '',
    seg?.complicacionesLargoPlazoNota ?? '',
    ...(seg?.complicacionesLargoPlazo ?? []),
    ...(seg?.eventos?.flatMap((e) => [e.nota, e.tipo, e.fecha]) ?? []),
    ficha?.resumen ?? '',
    ficha?.tecnicaDetalle ?? '',
    ficha?.complicacionesNota ?? '',
    ficha?.seguimientoNota ?? '',
    ficha?.hallazgosNota ?? '',
    ...(ficha?.pasosClave ?? []),
    ...(ficha?.puntosCriticos ?? []),
    ...(ficha?.lecciones ?? []),
  ];
}

export function matchesQuery(
  c: Surgery,
  query: string,
  opts?: QueryMatchOptions,
): boolean {
  const q = normalize(query.trim());
  if (!q) return true;
  if (surgerySearchHaystack(c).some((field) => field && normalize(field).includes(q))) {
    return true;
  }

  const hint = c.paciente.historiaClinicaHint?.trim();
  if (hint) {
    const hintN = normalize(hint);
    const qId = normalizePatientIdQuery(query);
    if (qId && (hintN.includes(qId) || qId.includes(hintN) || q.includes(hintN))) {
      return true;
    }
  }

  const stored = c.paciente.historiaClinica?.trim();
  if (stored) {
    const lookup = patientIdLookupToken(stored);
    if (opts?.patientIdHash && lookup === opts.patientIdHash) return true;
    // Legacy plaintext still on disk (pre-seal rows).
    if (
      !lookup &&
      !isPatientIdHash(stored) &&
      normalize(stored).includes(q)
    ) {
      return true;
    }
  }

  return false;
}

/* ────────────────────────── filtering ────────────────────────── */

export function filterSurgeries(
  surgeries: Surgery[],
  f: LibraryFilters,
  opts?: QueryMatchOptions,
): Surgery[] {
  let r = surgeries;
  if (f.query.trim()) r = r.filter((c) => matchesQuery(c, f.query, opts));
  // Precise range wins over year/month facets (toolbar sync writes both).
  const hasRange = isIsoDate(f.dateFrom) || isIsoDate(f.dateTo);
  if (hasRange) {
    if (isIsoDate(f.dateFrom)) r = r.filter((c) => c.fecha >= f.dateFrom!);
    if (isIsoDate(f.dateTo)) r = r.filter((c) => c.fecha <= f.dateTo!);
  } else {
    if (f.years.length > 0) r = r.filter((c) => f.years.includes(yearOf(c)));
    if (f.month != null) r = r.filter((c) => monthOf(c) === (f.month as number) - 1);
  }
  if (f.category) r = r.filter((c) => c.categoria === f.category);
  if (f.techniques.length > 0) r = r.filter((c) => f.techniques.includes(c.tecnica));
  if (f.role !== 'all') r = r.filter((c) => c.rol === f.role);
  if (f.complications !== 'all') {
    r = r.filter((c) =>
      f.complications === 'with' ? c.complicaciones.hubo : !c.complicaciones.hubo,
    );
  }
  if (f.newTechniquesYear != null) {
    const year = f.newTechniquesYear;
    const fresh = new Set(newTechniques(surgeries, year));
    r = r.filter((c) => yearOf(c) === year && fresh.has(c.tecnica));
  }
  if (f.needsEnrichment) r = r.filter(needsEnrichment);
  return r;
}

/* ────────────────────────── sort and grouping ────────────────────────── */

export function sortSurgeries(list: Surgery[], order: SortOrder): Surgery[] {
  const copy = [...list];
  switch (order) {
    case 'recent':
      return copy.sort((a, b) => b.fecha.localeCompare(a.fecha) || b.folio.localeCompare(a.folio));
    case 'oldest':
      return copy.sort((a, b) => a.fecha.localeCompare(b.fecha) || a.folio.localeCompare(b.folio));
    case 'duration':
      return copy.sort((a, b) => b.duracionMin - a.duracionMin || b.fecha.localeCompare(a.fecha));
    case 'folio':
      return copy.sort((a, b) => a.folio.localeCompare(b.folio, 'en'));
  }
}

export interface YearGroup {
  year: number;
  items: Surgery[];
}

/** Groups by year ("shelves"); the list must arrive already sorted */
export function groupByYear(list: Surgery[], direction: 'asc' | 'desc'): YearGroup[] {
  const map = new Map<number, Surgery[]>();
  for (const c of list) {
    const y = yearOf(c);
    const group = map.get(y);
    if (group) group.push(c);
    else map.set(y, [c]);
  }
  return [...map.entries()]
    .sort((a, b) => (direction === 'desc' ? b[0] - a[0] : a[0] - b[0]))
    .map(([year, items]) => ({ year, items }));
}

/* ────────────────────────── filter state ────────────────────────── */

export function hasActiveFilters(f: LibraryFilters): boolean {
  return countFilters(f) > 0;
}

/** Number of active filter chips (mobile "Filters (n)" badge) */
export function countFilters(f: LibraryFilters): number {
  const hasRange = Boolean(f.dateFrom || f.dateTo);
  return (
    (f.query.trim() ? 1 : 0) +
    // Range supersedes year/month in the chip count (same as filterSurgeries).
    (hasRange ? 1 : f.years.length + (f.month != null ? 1 : 0)) +
    (f.category ? 1 : 0) +
    f.techniques.length +
    (f.role !== 'all' ? 1 : 0) +
    (f.complications !== 'all' ? 1 : 0) +
    (f.newTechniquesYear != null ? 1 : 0) +
    (f.needsEnrichment ? 1 : 0)
  );
}

/* ─────────────────────── query params (contract with Dashboard/Report) ─────────────────────── */

export const KNOWN_PARAMS = [
  'year',
  'month',
  'from',
  'to',
  'category',
  'complications',
  'newTechniques',
  'enrich',
] as const;

/**
 * Turns query params into initial filter state.
 * `fallbackYear`: most recent year with data, for `newTechniques=1` without `year`.
 */
export function filtersFromParams(
  params: URLSearchParams,
  fallbackYear: number | null,
): LibraryFilters {
  const f: LibraryFilters = { ...EMPTY_FILTERS, years: [], techniques: [] };

  const rawYear = params.get('year');
  if (rawYear && /^\d{4}$/.test(rawYear)) f.years = [Number(rawYear)];

  const rawMonth = params.get('month');
  if (rawMonth) {
    const month = Number(rawMonth);
    if (Number.isInteger(month) && month >= 1 && month <= 12) f.month = month;
  }

  const from = params.get('from');
  if (isIsoDate(from)) {
    f.dateFrom = from;
    f.month = null;
    f.years = [];
  }
  const to = params.get('to');
  if (isIsoDate(to)) {
    f.dateTo = to;
    f.month = null;
    f.years = [];
  }

  // Year/month deep-links become an open range through today (matches toolbar).
  if (!f.dateFrom && !f.dateTo && f.years.length > 0) {
    const range = dateRangeFromYearsMonth(f.years, f.month);
    f.dateFrom = range.dateFrom;
    f.dateTo = range.dateTo;
  }

  const category = params.get('category');
  if (category && category.trim()) f.category = category;

  if (params.get('complications') === '1') f.complications = 'with';

  if (params.get('newTechniques') === '1') {
    f.newTechniquesYear = f.years[0] ?? fallbackYear;
  }

  if (params.get('enrich') === '1') {
    f.needsEnrichment = true;
    // Show the full career queue, not only the current calendar year.
    if (!params.has('year') && !params.has('from') && !params.has('to')) {
      f.years = [];
      f.month = null;
      f.dateFrom = null;
      f.dateTo = null;
    }
  }

  return f;
}

/* ────────────────────────── session restore ────────────────────────── */

/** Validates an unknown object against the LibraryFilters shape */
export function sanitizeFilters(value: unknown): LibraryFilters {
  const f = (value ?? {}) as Partial<LibraryFilters>;
  return {
    query: typeof f.query === 'string' ? f.query : '',
    years: Array.isArray(f.years) ? f.years.filter((a) => Number.isInteger(a)) : [],
    month:
      typeof f.month === 'number' && Number.isInteger(f.month) && f.month >= 1 && f.month <= 12
        ? f.month
        : null,
    dateFrom: isIsoDate(f.dateFrom) ? f.dateFrom : null,
    dateTo: isIsoDate(f.dateTo) ? f.dateTo : null,
    category: typeof f.category === 'string' && f.category ? f.category : null,
    techniques: Array.isArray(f.techniques)
      ? f.techniques
          .map((t) => normalizeTechnique(t))
          .filter((t): t is Technique => TECHNIQUES.includes(t))
      : [],
    role: sanitizeRoleFilter(f.role),
    complications: sanitizeComplicationFilter(f.complications),
    newTechniquesYear:
      typeof f.newTechniquesYear === 'number' && Number.isInteger(f.newTechniquesYear)
        ? f.newTechniquesYear
        : null,
    needsEnrichment: f.needsEnrichment === true,
  };
}

/** Accepts current and legacy (Spanish) stored sort values. */
export function sanitizeSort(value: unknown): SortOrder {
  if (SORT_ORDERS.some((o) => o.value === value)) return value as SortOrder;
  if (value === 'recientes') return 'recent';
  if (value === 'antiguas') return 'oldest';
  if (value === 'duracion') return 'duration';
  return 'recent';
}

/** Accepts current and legacy (Spanish) stored complication-filter values. */
export function sanitizeComplicationFilter(value: unknown): ComplicationFilter {
  if (value === 'with' || value === 'without') return value;
  if (value === 'con') return 'with';
  if (value === 'sin') return 'without';
  return 'all';
}

/** Accepts current and legacy (Spanish) stored role-filter values. */
export function sanitizeRoleFilter(value: unknown): Role | 'all' {
  if (value === 'all' || value === 'todos') return 'all';
  if (typeof value !== 'string') return 'all';
  if ((ROLES as string[]).includes(value)) return value as Role;
  return LEGACY_ROLE_VALUES[value] ?? 'all';
}
